import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AbstractControl,
    FormBuilder,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { PasswordResetService } from '../../services/password-reset.service';

@Component({
    selector: 'app-reset-password',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterLink,
        MatButtonModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent {
    private readonly fb = inject(FormBuilder);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly passwordResetService = inject(PasswordResetService);

    protected readonly hidePassword = signal(true);
    protected readonly hideConfirmPassword = signal(true);
    protected readonly isSubmitting = signal(false);
    protected readonly serverError = signal<string | null>(null);
    protected readonly successMessage = signal<string | null>(null);

    protected readonly email = signal(this.route.snapshot.queryParamMap.get('email') ?? '');
    protected readonly token = signal(this.route.snapshot.queryParamMap.get('token') ?? '');

    protected readonly hasValidResetLink = signal(!!this.email() && !!this.token());

    protected readonly form = this.fb.nonNullable.group(
        {
            newPassword: ['', [Validators.required, Validators.minLength(8)]],
            confirmPassword: ['', [Validators.required]],
        },
        {
            validators: [this.passwordsMatchValidator],
        }
    );

    protected togglePasswordVisibility(): void {
        this.hidePassword.update((value) => !value);
    }

    protected toggleConfirmPasswordVisibility(): void {
        this.hideConfirmPassword.update((value) => !value);
    }

    protected submit(): void {
        this.serverError.set(null);
        this.successMessage.set(null);

        if (!this.hasValidResetLink()) {
            this.serverError.set(
                'Der Link zum Zurücksetzen des Passworts ist ungültig oder unvollständig.'
            );
            return;
        }

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.isSubmitting.set(true);

        this.passwordResetService
            .resetPassword({
                email: this.email(),
                token: this.token(),
                newPassword: this.form.controls.newPassword.value,
            })
            .subscribe({
                next: () => {
                    this.isSubmitting.set(false);
                    this.successMessage.set('Ihr Passwort wurde erfolgreich aktualisiert.');

                    setTimeout(() => {
                        this.router.navigate(['/login']);
                    }, 1500);
                },
                error: () => {
                    this.isSubmitting.set(false);
                    this.serverError.set(
                        'Das Passwort konnte nicht aktualisiert werden. Der Link ist möglicherweise abgelaufen oder ungültig.'
                    );
                },
            });
    }

    private passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
        const newPassword = control.get('newPassword')?.value;
        const confirmPassword = control.get('confirmPassword')?.value;

        if (!newPassword || !confirmPassword) {
            return null;
        }

        return newPassword === confirmPassword ? null : { passwordsNotMatching: true };
    }
}